/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {resolveDeviceName} from '@ion/core/src/entities/Devices';
import {Device} from '@ion/core/src/types/entities';
/**
 * The `useDeviceName` hook provides a simple convenience function which returns a
 * memoized  `name` value to be used when referencing a `Device` name and
 * based on the `fallback` value returns other unique identifier values if name and
 * deviceName is not available. This hook simply utilizes the @{link resolveDeviceName}
 * utility, and returns the memoized result.
 * ...
 * <describe scenarios as to how and why a device may have a different attribute
 * used for name>
 * ...
 *
 * ```typescript
 *   import {useDeviceName} from `@ion/dls/src/hooks`;
 *
 *   const name = useDeviceName(device); // either device.name, or device.deviceName
 *
 * ```
 * @param device A device instance from which to resolve it's name value
 * @param fallback if true utility checks the device.name or device.devicename is available
 * @returns The (memoized) device name value, either `name` or falling back to
 *          `deviceName` or based on `fallback` returns other unique identifier values..
 */
export const useDeviceName = (device: Device, fallback = false): string | number => {
  const name = useMemo(() => {
    return resolveDeviceName(device, fallback);
  }, [device]);

  return name;
};
